import { getCharacterPlatformBackendUrl } from "@/lib/backend/server"
import { buildBackendRequestError } from "@/lib/backend/error-details.js"

export type ChatMessage = {
  role: string
  content: string
}

export type ChatSendResult = {
  text: string
  run_id: string
}

export type ChatTaskArtifact = {
  type?: "media"
  storage_path: string
  mime?: string | null
  kind?: "image" | "gif" | "video" | "audio" | null
  filename?: string | null
  size_bytes?: number | null
  signed_url?: string | null
}

export type ChatTaskResult = {
  text: string
  run_id: string
  artifacts: ChatTaskArtifact[]
}

export type ChatHistoryResult = {
  messages: ChatMessage[]
}

export type MasterChatOrchestrationAssignment = {
  agent_id: string
  agent_name: string
  instruction: string
  label: string
}

export type MasterChatOrchestrationResult = {
  mode: "chat" | "task"
  assignments: MasterChatOrchestrationAssignment[]
  planner_model: string
}

export async function chatSend(
  accessToken: string,
  message: string,
  agentId?: string | null,
  timeoutMs = 120_000
): Promise<ChatSendResult> {
  const response = await fetch(
    new URL("/v1/openclaw/chat/send", getCharacterPlatformBackendUrl()),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        message,
        agent_id: agentId ?? null,
        timeout_ms: timeoutMs,
      }),
      cache: "no-store",
    }
  )

  if (!response.ok) {
    throw await buildBackendRequestError(response, "Failed to send message.")
  }

  return (await response.json()) as ChatSendResult
}

export async function chatHistory(
  accessToken: string,
  agentId?: string | null,
  limit = 50
): Promise<ChatHistoryResult> {
  const url = new URL(
    "/v1/openclaw/chat/history",
    getCharacterPlatformBackendUrl()
  )
  if (agentId) url.searchParams.set("agent_id", agentId)
  url.searchParams.set("limit", String(limit))

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
    cache: "no-store",
  })

  if (!response.ok) {
    throw await buildBackendRequestError(
      response,
      "Failed to fetch chat history."
    )
  }

  return (await response.json()) as ChatHistoryResult
}

export async function chatSendStream(
  accessToken: string,
  message: string,
  agentId: string | null | undefined,
  onChunk: (text: string) => void,
  signal?: AbortSignal,
  timeoutMs = 120_000
): Promise<void> {
  const response = await fetch(
    new URL("/v1/openclaw/chat/stream", getCharacterPlatformBackendUrl()),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        message,
        agent_id: agentId ?? null,
        timeout_ms: timeoutMs,
      }),
      cache: "no-store",
      signal,
    }
  )

  if (!response.ok) {
    throw await buildBackendRequestError(response, "Failed to start stream.")
  }

  if (!response.body) throw new Error("No response body.")

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buf = ""
  let currentEvent = ""

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buf += decoder.decode(value, { stream: true })
      const lines = buf.split("\n")
      buf = lines.pop() ?? ""

      for (const raw of lines) {
        const line = raw.trimEnd()
        if (!line) {
          currentEvent = ""
          continue
        }
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim()
          continue
        }
        if (!line.startsWith("data:")) continue

        let data: unknown
        try {
          data = JSON.parse(line.slice(5).trim())
        } catch {
          continue
        }

        if (currentEvent === "done") return
        if (currentEvent === "error")
          throw new Error((data as { error?: string }).error ?? "Stream error")
        onChunk((data as { text?: string }).text ?? "")
      }
    }
  } finally {
    reader.releaseLock()
  }
}

export async function chatRunTask(
  accessToken: string,
  message: string,
  agentId: string | null | undefined,
  taskId?: string | null,
  timeoutMs = 600_000
): Promise<ChatTaskResult> {
  const response = await fetch(
    new URL("/v1/openclaw/chat/task", getCharacterPlatformBackendUrl()),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        message,
        agent_id: agentId ?? null,
        task_id: taskId ?? null,
        timeout_ms: timeoutMs,
      }),
      cache: "no-store",
    }
  )

  if (!response.ok) {
    throw await buildBackendRequestError(
      response,
      "Failed to run OpenClaw task."
    )
  }

  return (await response.json()) as ChatTaskResult
}

export async function masterChatOrchestrate(
  accessToken: string,
  message: string,
  mentionedAgentId?: string | null
): Promise<MasterChatOrchestrationResult> {
  const response = await fetch(
    new URL("/v1/openclaw/chat/orchestrate", getCharacterPlatformBackendUrl()),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        message,
        mentioned_agent_id: mentionedAgentId ?? null,
      }),
      cache: "no-store",
    }
  )

  if (!response.ok) {
    throw await buildBackendRequestError(
      response,
      "Master chat orchestration failed."
    )
  }

  return (await response.json()) as MasterChatOrchestrationResult
}

export async function chatReset(
  accessToken: string,
  agentId?: string | null
): Promise<void> {
  const url = new URL(
    "/v1/openclaw/chat/reset",
    getCharacterPlatformBackendUrl()
  )
  if (agentId) url.searchParams.set("agent_id", agentId)

  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
    cache: "no-store",
  })

  if (!response.ok) {
    throw await buildBackendRequestError(
      response,
      "Failed to reset chat session."
    )
  }
}
